import { createRequestGate } from "./net/request-gate";

/**
 * Module-level singleton gate for SWR GETs.
 *
 * SCOPE (important): this gate governs ONLY the SWR GETs routed through
 * `fetcher` below. It caps concurrent in-flight app API GETs and de-dupes
 * identical in-flight GETs (keyed by URL) so no page can recreate the
 * request-storm pattern that saturated the browser's ~6-connection pool.
 *
 * Non-GET requests are intentionally OUT OF SCOPE: many mutations across the
 * app call native `fetch` directly and do not pass through here, so this gate
 * does not (and does not claim to) govern them. A true global cap would require
 * a shared `appFetch` wrapper — a separate, separately-sized effort.
 *
 * This is an in-flight (concurrent) dedupe/cap and is complementary to SWR's
 * own time-window `dedupingInterval`; it does not replace or change it.
 *
 * NOT A HARD POOL CAP (bypass): `max` bounds only the GATED subset — GETs that
 * go through this default `fetcher`. Native `fetch` mutations and any GET that
 * doesn't route through here (e.g. a direct `/api/documents/status` poll)
 * BYPASS the gate entirely. So `max: 4` is NOT a full browser
 * connection-pool bound; do not rely on it as a hard cap on total in-flight
 * requests.
 *
 * FIFO / NO PRIORITY: the gate's wait queue is strict FIFO with no
 * interactive-vs-background priority. On a cold load, a user-initiated GET can
 * queue behind secondary/background GETs dispatched first. Tuning `max` or
 * adding a priority lane is a follow-up pending live instrumentation — keep
 * `max: 4` as-is until we have real concurrency measurements.
 *
 * MUTATE VS IN-FLIGHT DEDUPE (narrow staleness window): because dedupe is
 * URL-keyed on in-flight GETs, a forced `mutate()` revalidation issued in the
 * tiny window while an earlier GET for the same URL is still in flight can be
 * collapsed onto that earlier request — which may have been dispatched just
 * before a write committed, yielding a transient stale read. This narrows SWR's
 * existing `dedupingInterval` behavior; the windows are small, but a maintainer
 * relying on forced-revalidation freshness should be aware of it.
 */
const gate = createRequestGate({ max: 4 });

export async function fetcher<T>(url: string): Promise<T> {
  // Keyed by URL: a URL uniquely identifies a GET, so concurrent callers for
  // the same URL share one in-flight request/result.
  return gate.run(url, async () => {
    const res = await fetch(url);
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || `Fetch failed: ${res.status}`);
    }
    return res.json() as Promise<T>;
  });
}
